import { Injectable } from '@angular/core';
import { AuthenticationResult, BrowserSystemOptions, LogLevel } from '@azure/msal-browser';
import { from, map, Observable, Subscription, timer } from 'rxjs';
import { User } from '../models/User';

@Injectable()
export abstract class AuthenticationService {
  protected isInteractionRequired = false;
  protected browserSystemOptions: Partial<BrowserSystemOptions>;
  protected registeredScopesByServiceKey: Map<string, string> = new Map();

  private _timerSubscription?: Subscription;

  constructor() {
    this.browserSystemOptions = {
      loggerOptions: {
        loggerCallback: this.handleLogMessage,
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
      },
    };
  }

  /**
   * Initializes the authentication service.
   */
  public abstract initializeAsync(): Observable<void>;

  /**
   * Verifies that the user login is valid and therefore neither interaction is
   * required nor has the session timed out.
   *
   * @return true if the login is valid, false otherwise
   */
  public abstract isUserLoginValid(): boolean;

  /**
   * Gets the current user or null if the user is not logged in.
   *
   * @return A portal user or null.
   */
  public abstract getCurrentUser(): User | null;

  /**
   * Gets the current user or an expired default one.
   *
   * @return A portal user.
   */
  public getCurrentUserOrDefault(): User {
    const currentUser = this.getCurrentUser();

    return currentUser ?? User.createDefault();
  }

  /**
   * Triggers the login functionality.
   */
  public abstract login(loginHint?: string): void;

  /**
   * Triggers the switch account functionality (Prompting for account selection).
   */
  public abstract changeAccount(): void;

  /**
   * Logs out the current user and clears the session store.
   */
  public abstract logout(): void;

  /**
   * Enables or disables the logout prevention by periodically renewing the token.
   */
  public async preventLogoutAsync(enabled: boolean): Promise<void> {
    if (!enabled) {
      console.log('No longer renewing access token.');
      return this._timerSubscription?.unsubscribe();
    }

    return await this.preventLogoutInternalAsync();
  }

  /**
   * Gets the user's access token asynchronous.
   * Tries to renew the token automatically.
   *
   * @param serviceKey A service key where the token is requested / renewed for. The scope needs to be registered before initialization.
   * @return Observable holding either the user's token or null if the token could not be recoverd.
   */
  public getAccessTokenAsync(serviceKey?: string): Observable<string | null> {
    const scopes = this.getScopes(serviceKey);
    return from(this.getAuthenticationResultAsync(scopes)).pipe(map(r => r?.accessToken ?? null));
  }

  protected abstract getScopes(serviceKey?: string): string;

  protected abstract getAuthenticationResultAsync(
    scopes: string
  ): Promise<AuthenticationResult | null>;

  /**
   * Registers an additional scope that will be consumed by the application. Registered scopes are
   * initialized during the authentication and can be loaded by getScope.
   *
   * @param serviceKey A service key
   * @param scope The scope
   */
  public registerScope(serviceKey: string, scope: string): void {
    if (scope?.length > 0) {
      this.registeredScopesByServiceKey.set(serviceKey, scope);
    }
  }

  private handleLogMessage(level: LogLevel, message: string, containsPii: boolean): void {
    console.log(`MSAL (${LogLevel[level]}): ${message} ${containsPii ? '(PII)' : ''}`);
  }

  private async preventLogoutInternalAsync(): Promise<void> {
    const authenticationResult = await this.getAuthenticationResultAsync(this.getScopes());
    if (authenticationResult?.expiresOn) {
      const date = new Date(authenticationResult.expiresOn);
      date.setTime(date.getTime() - 1 * 60 * 1000);
      console.log(
        `Renewed access token on ${new Date()}, its valid until ${
          authenticationResult.expiresOn
        } and will be renewed on ${date}.`
      );

      if (this._timerSubscription) {
        this._timerSubscription.unsubscribe();
      }

      this._timerSubscription = timer(date).subscribe({
        next: () => {
          this.preventLogoutInternalAsync().catch(error => {
            console.error('Error in preventLogoutInternalAsync:', error);
          });
        },
      });
    }
  }
}
